/*
 * Copyright (c) 2023 CalAmp Corp. All Rights Reserved
 */
import React, {memo, useCallback, useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useCurrentGlobalGroupId} from '@ion/applications/src/hooks/useCurrentGlobalGroupId';
import {translate} from '@ion/core/src/locale/locale';
import EditorDialog from '@ion/dls/src/editors/EditorDialog';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {DLSTheme} from '@ion/dls/src/types/types';
import BulkViewDeviceContainer from './BulkViewDeviceContainer';
import {
  loadDeviceDetail,
  moveAdminDevices,
  resetDevicesTab,
  showBulkViewDevices,
  viewAdminDevice,
  viewDeviceById
} from './state';
import {
  viewDevicesSelectedSelector,
  viewDevicesSelectedTabSelector,
  viewDevicesShowDialogSelector
} from './state-selectors';
import {ViewDeviceDialogProps} from './ViewDeviceDialog';

export const MAX_VIEW_DEVICES = 10;

const overrides = (theme: DLSTheme) => ({
  formPaper : {
    backgroundColor                : `${dlsColor('grey6')} !important`,
    maxWidth                       : '96%',
    minWidth                       : '96%',
    overflow                       : 'hidden',
    margin                         : 'auto',
    [theme.breakpoints.down('sm')] : {
      width     : '100%',
      maxHeight : '100% !important',
      margin    : '0px'
    }
  },
  paperScrollPaper : {
    maxHeight : 'calc(100% - 94px)',
    minHeight : '96vh'
  },
  scrollPaper : {
    flexDirection  : 'column',
    justifyContent : 'flex-end'
  },
  formDialogContent : {
    overflowX  : 'hidden',
    paddingTop : '0px'
  }
});

/**
 * Holds dialog functionality for Bulk View Device.
 */
export const BulkViewDevicesDialog = ({
  selectedJobId,
  devicesPermissions,
  hasCreateJob,
  Features,
  explicitReportDate = false,
  includeEditAction = false,
  refreshDevice = false,
  enableViewDialog = false,
  isToggleSwitchGrid = false,
  isVbuFwEnabled = false,
  isShowColumnChooserIcon = false,
  isCtcAdmin = false,
  isShowRefreshButton = true
}: ViewDeviceDialogProps) => {
  const dispatch        = useDispatch();
  const showViewDevices = useSelector(viewDevicesShowDialogSelector);
  const selectedDevices = useSelector(viewDevicesSelectedSelector);
  const selectedTab     = useSelector(viewDevicesSelectedTabSelector);
  const accountId       = useCurrentGlobalGroupId();

  const onCloseDialog = useCallback(() => {
    dispatch(showBulkViewDevices(null, false));
    dispatch(resetDevicesTab());
    dispatch(viewAdminDevice(null, false));
    dispatch(loadDeviceDetail(null));
    dispatch(moveAdminDevices(null, false));
  }, []);

  useEffect(() => {
    const deviceId = selectedTab?.id;
    deviceId && dispatch(viewDeviceById(deviceId) as any);
  }, [selectedTab]);

  if (showViewDevices && selectedDevices && enableViewDialog) {
    return (
      <EditorDialog open
        dialogTitle={translate('admin-devices:dialogBulkView.title')}
        onCloseDialog={onCloseDialog}
        onCancelDialog={onCloseDialog}
        styleOverrides={overrides}
        omitActions>
        <BulkViewDeviceContainer accountId={accountId}
          refreshDevice={refreshDevice}
          Features={Features}
          devicesPermissions={devicesPermissions}
          hasCreateJob={hasCreateJob}
          isVbuFwEnabled={isVbuFwEnabled}
          selectedJobId={selectedJobId}
          includeEditAction={includeEditAction}
          explicitReportDate={explicitReportDate}
          isToggleSwitchGrid={isToggleSwitchGrid}
          isShowColumnChooserIcon={isShowColumnChooserIcon}
          isCtcAdmin={isCtcAdmin}
          isShowRefreshButton={isShowRefreshButton}/>
      </EditorDialog>
    );
  } else {
    return <div />;
  }
};

export default memo(BulkViewDevicesDialog);
